import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowRight, ArrowUp, Minus } from 'lucide-react';
import { Botao, Selo } from '../base';
import { anunciarOcultos } from '../sensivel';
import { TextoCortado } from '../texto-cortado';
import { data, dinheiro, mesAno, nomePessoa, numero, relativo, textoLivre } from '../../nucleo/formato';
import { moduloWeb } from '../../nucleo/modulos-web';
import { rotuloAcao, rotuloCategoria, rotuloEntidade } from '../../nucleo/rotulos';
import { GraficoFluxo } from './GraficoFluxo';
import { iconeDoIndicador, variacao, type IndicadorApi, type ItemIndicador, type ParteIndicador, type ValorPorMoeda } from './tipos';

type Formato = 'numero' | 'dinheiro' | 'percentual' | 'texto';

/** Valor no formato do card; dinheiro e nome passam pelo modo discreto (formato.ts). */
function formatar(formato: Formato, valor: number | string | null | undefined, moeda: string): string {
  if (valor === null || valor === undefined) return '';
  if (formato === 'dinheiro') return dinheiro(Number(valor), moeda);
  if (formato === 'numero') return numero(Number(valor));
  if (formato === 'percentual') return `${numero(Number(valor), 1)}%`;
  return String(valor);
}

/** Outras moedas abaixo do valor principal (D25: uma linha por moeda, nunca somadas). */
function OutrasMoedas({ linhas, principal }: { linhas: ValorPorMoeda[]; principal: string }) {
  const outras = linhas.filter((l) => l.moeda !== principal);
  if (!outras.length) return null;
  return (
    <ul className="mt-1 flex flex-col gap-0.5">
      {outras.map((l) => (
        <li key={l.moeda} className="numero text-sm text-tinta-2">
          {anunciarOcultos(dinheiro(l.valor, l.moeda))}
        </li>
      ))}
    </ul>
  );
}

/** Delta com seta E texto (nunca só cor), contra o período anterior de mesma duração. */
function Delta({ i, moeda }: { i: IndicadorApi; moeda: string }) {
  const { t } = useTranslation();
  if (!i.comparar || typeof i.valor !== 'number' || i.anterior === undefined || i.anterior === null) return null;
  const v = variacao(i.valor, i.anterior);
  const titulo = t('inicio.delta.anterior', { valor: formatar(i.formato, i.anterior, moeda) });
  if (v === null || Math.abs(v) < 0.05) {
    const texto = v === null && i.valor !== i.anterior ? t('inicio.delta.semAnterior') : t('inicio.delta.igual');
    return (
      <p className="mt-2 flex items-center gap-1 text-xs text-tinta-3" title={titulo}>
        <Minus className="size-3.5 shrink-0" aria-hidden />
        {anunciarOcultos(texto)}
      </p>
    );
  }
  const subiu = v > 0;
  const bom = i.comparar === 'subir_bom' ? subiu : !subiu;
  const Seta = subiu ? ArrowUp : ArrowDown;
  return (
    <p className={`mt-2 flex items-center gap-1 text-xs font-medium ${bom ? 'text-verde' : 'text-perigo'}`} title={titulo}>
      <Seta className="size-3.5 shrink-0" aria-hidden />
      {t(subiu ? 'inicio.delta.acima' : 'inicio.delta.abaixo', { pct: `${numero(Math.abs(v), 1)}%` })}
    </p>
  );
}

function ValorParte({ p, formato, moeda }: { p: ParteIndicador; formato: Formato; moeda: string }) {
  const f = p.formato ?? formato;
  const outras = f === 'dinheiro' ? (p.por_moeda ?? []).filter((l) => l.moeda !== moeda) : [];
  // Uma moeda por linha (D25), alinhadas à direita.
  return (
    <>
      <span className="block">{anunciarOcultos(formatar(f, p.valor, moeda))}</span>
      {outras.map((l) => (
        <span key={l.moeda} className="block">
          {anunciarOcultos(dinheiro(l.valor, l.moeda))}
        </span>
      ))}
    </>
  );
}

/** Números secundários do card (faixas, minhas e sem dono, atrasados), cada um com o seu link quando há. */
function Partes({ i, moeda }: { i: IndicadorApi; moeda: string }) {
  const { t } = useTranslation();
  if (!i.partes?.length) return null;
  const tom = (p: ParteIndicador) => (p.tom === 'perigo' && p.valor ? 'text-perigo' : p.tom === 'atencao' && p.valor ? 'text-laranja' : 'text-tinta-1');
  return (
    <dl className="mt-3 flex flex-col gap-1.5 border-t border-borda pt-3">
      {i.partes.map((p) => {
        const rotulo = t(`inicio.cards.${i.chave}.partes.${p.chave}`, { defaultValue: p.chave });
        return (
          <div key={p.chave} className="flex min-w-0 items-baseline justify-between gap-3">
            <dt className="min-w-0 text-xs text-tinta-3">{p.link ? <Link to={p.link} className="-my-1 inline-block py-1 underline-offset-2 hover:text-tinta-1 hover:underline">{rotulo}</Link> : rotulo}</dt>
            <dd className={`numero text-right text-sm font-medium wrap-anywhere ${tom(p)}`}>
              <ValorParte p={p} formato={(i.formato as Formato) ?? 'numero'} moeda={moeda} />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** Linha de card em lista: o texto vem do dado e passa pelo formatador do tipo (nome de parceiro pelo modo discreto). */
function LinhaLista({ i, item, moeda }: { i: IndicadorApi; item: ItemIndicador; moeda: string }) {
  const { t } = useTranslation();
  const titulo =
    i.chave === 'atividade_recente'
      ? `${rotuloAcao(item.rotulo ?? '')} · ${rotuloEntidade(item.titulo)}`
      : item.rotulo === 'sem_categoria' || item.rotulo === 'outras'
        ? t(`inicio.lista.${item.rotulo}`)
        : item.tipo_titulo === 'pessoa'
          ? nomePessoa(item.titulo)
          : item.titulo;
  const detalhe =
    i.chave === 'atividade_recente'
      ? [item.autor ?? t(`inicio.lista.autor.${item.autor_tipo === 'chave_api' ? 'chave' : 'sistema'}`), item.data ? relativo(item.data) : ''].filter(Boolean).join(' · ')
      : i.chave === 'proximos_vencimentos'
        ? `${t(`inicio.lista.${item.rotulo === 'conta_pagar' ? 'conta_pagar' : 'fatura'}`)} · ${item.data ? data(item.data) : ''}`
        : item.dias !== undefined
          ? t('inicio.lista.diasAtraso', { count: item.dias })
          : '';
  const valor = item.valor !== undefined && i.chave !== 'atividade_recente' ? dinheiro(item.valor, item.moeda ?? moeda) : '';
  const conteudo = (
    <>
      <span className="min-w-0 flex-1">
        <TextoCortado className="block text-sm font-medium text-tinta-1">{titulo}</TextoCortado>
        {detalhe && <TextoCortado className="block text-xs text-tinta-3">{detalhe}</TextoCortado>}
      </span>
      {valor && <span className="numero shrink-0 text-sm text-tinta-1">{anunciarOcultos(valor)}</span>}
    </>
  );
  // Fase 2 (W21, K7): ação da linha publicada pelo módulo dono do card (baixa rápida do Contas), ao lado do link.
  const Acao = moduloWeb(i.modulo)?.acoesItemInicio?.[i.chave];
  return (
    <li className={Acao ? 'flex min-w-0 items-center gap-1' : undefined}>
      {item.link ? (
        <Link to={item.link} className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1.5 hover:bg-sup-2">
          {conteudo}
        </Link>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3 px-2 py-1.5">{conteudo}</div>
      )}
      {Acao && <Acao itemId={item.id} nome={item.tipo_titulo === 'pessoa' ? nomePessoa(item.titulo) : textoLivre(item.titulo)} rotulo={item.rotulo} />}
    </li>
  );
}

/** Despesas por categoria: barra proporcional (acento-forte) além do valor. */
function LinhaCategoria({ item, total, moeda }: { item: ItemIndicador; total: number; moeda: string }) {
  const { t } = useTranslation();
  const nome = item.rotulo === 'sem_categoria' || item.rotulo === 'outras' ? t(`inicio.lista.${item.rotulo}`) : rotuloCategoria(item.titulo);
  const pct = total > 0 ? Math.max(2, Math.round(((item.valor ?? 0) / total) * 100)) : 0;
  return (
    <li className="flex flex-col gap-1 px-2 py-1">
      <span className="flex min-w-0 items-baseline justify-between gap-3">
        <TextoCortado className="min-w-0 text-sm text-tinta-1">{nome}</TextoCortado>
        <span className="numero shrink-0 text-sm text-tinta-1">{anunciarOcultos(dinheiro(item.valor ?? 0, item.moeda ?? moeda))}</span>
      </span>
      <span className="h-1.5 w-full rounded-full bg-sup-3" aria-hidden>
        <span className="block h-1.5 rounded-full bg-acento-forte" style={{ width: `${pct}%` }} />
      </span>
    </li>
  );
}

function Corpo({ i, moeda, rotuloPeriodo }: { i: IndicadorApi; moeda: string; rotuloPeriodo: string }) {
  const { t } = useTranslation();
  const formato = (i.formato as Formato) ?? 'numero';
  const principal = i.por_moeda?.[0]?.moeda ?? moeda;
  const vazio = t(`inicio.cards.${i.chave}.vazio`, { defaultValue: t(i.apresentacao === 'lista' ? 'inicio.vazio.lista' : 'inicio.vazio.padrao') });
  if (i.apresentacao === 'lista') {
    const lista = i.lista ?? [];
    if (!lista.length) return <p className="mt-3 text-sm text-tinta-3">{vazio}</p>;
    if (i.chave === 'despesas_categoria') {
      const total = typeof i.valor === 'number' ? i.valor : 0;
      return (
        <>
          <p className="numero mt-2 text-2xl font-bold break-words">{anunciarOcultos(dinheiro(total, moeda))}</p>
          <p className="mt-1 text-xs text-tinta-3">{rotuloPeriodo}</p>
          <ul className="mt-3 -mx-2 flex flex-col gap-1">
            {lista.map((item) => (
              <LinhaCategoria key={item.id} item={item} total={total} moeda={moeda} />
            ))}
          </ul>
        </>
      );
    }
    return (
      <ul className="mt-2 -mx-2 flex flex-col">
        {lista.map((item) => (
          <LinhaLista key={item.id} i={i} item={item} moeda={moeda} />
        ))}
      </ul>
    );
  }
  if (i.apresentacao === 'grafico' && i.serie) {
    const mov = i.serie.pontos.some((p) => p.entradas || p.saidas);
    return (
      <>
        <p className="mt-2 text-xs text-tinta-3">{t(`inicio.cards.${i.chave}.valor`, { defaultValue: '' })}</p>
        <p className="numero text-2xl font-bold break-words">{anunciarOcultos(formatar('dinheiro', i.valor, i.serie.moeda))}</p>
        <p className="mt-1 text-xs text-tinta-3">{rotuloPeriodo}</p>
        <div className="mt-3">{mov ? <GraficoFluxo pontos={i.serie.pontos} moeda={i.serie.moeda} granularidade={i.serie.granularidade} /> : <p className="text-sm text-tinta-3">{t('inicio.vazio.grafico')}</p>}</div>
        <Partes i={i} moeda={i.serie.moeda} />
      </>
    );
  }
  if (i.valor === null || i.valor === undefined) return <p className="mt-3 text-sm text-tinta-3">{vazio}</p>;
  const sub = subtitulo(i, t);
  return (
    <>
      <p className="numero mt-2 text-2xl font-bold break-words">{anunciarOcultos(formatar(formato, i.valor, principal))}</p>
      {formato === 'dinheiro' && i.por_moeda && <OutrasMoedas linhas={i.por_moeda} principal={principal} />}
      {(sub || i.natureza === 'fluxo') && <p className="mt-1 text-xs text-tinta-3">{[i.natureza === 'fluxo' ? rotuloPeriodo : '', sub].filter(Boolean).join(' · ')}</p>}
      <Delta i={i} moeda={principal} />
      <Partes i={i} moeda={principal} />
    </>
  );
}

/** Subtítulo próprio do card, montado no idioma a partir do dado (`subtitulo`) que o módulo devolve. */
function subtitulo(i: IndicadorApi, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!i.subtitulo) return '';
  if (i.chave === 'folha_ultima') return t('inicio.cards.folha_ultima.subtitulo', { mes: mesAno(i.subtitulo) });
  if (i.chave === 'agenda_hoje') return t('inicio.cards.agenda_hoje.subtitulo', { hora: i.subtitulo });
  if (i.chave === 'saldo_contas') return t('inicio.cards.saldo_contas.subtitulo', { count: Number(i.subtitulo) });
  return '';
}

/**
 * Fase 2 (W1, A5 5.10): card do Início. Mesmo cartão do sistema (borda, superfície 1, raio xl), ícone em quadrado de
 * acento suave, etiqueta da base (hoje, agora ou a base do fluxo), valor em `.numero`, outras moedas em linhas
 * próprias, delta com seta e texto, partes, link e estados vazio e indisponível.
 */
export function CartaoIndicador({ i, moeda, rotuloPeriodo, aoTentar }: { i: IndicadorApi; moeda: string; rotuloPeriodo: string; aoTentar: () => void }) {
  const { t } = useTranslation();
  const Icone = iconeDoIndicador(i.icone);
  const rotulo = t(`inicio.cards.${i.chave}.rotulo`, { defaultValue: t(`${i.modulo}:indicadores.${i.chave}`, { defaultValue: i.rotulo }) });
  const etiqueta = i.natureza === 'fila' ? t('inicio.etiqueta.agora') : i.natureza === 'posicao' ? t('inicio.etiqueta.hoje') : i.base ? t(`inicio.base.${i.base}`) : t('inicio.etiqueta.periodo');
  const indisponivel = i.estado === 'indisponivel';
  return (
    <article
      data-indicador={i.chave}
      aria-label={rotulo}
      className={`flex min-w-0 flex-col rounded-xl border bg-sup-1 p-4 shadow-cartao ${indisponivel ? 'border-perigo' : 'border-borda'} ${i.largura === 2 ? 'sm:col-span-2' : ''}`}
    >
      <header className="flex min-w-0 items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-acento-suave text-acento-forte">
          <Icone className="size-4.5" aria-hidden />
        </span>
        <h3 className="min-w-0 flex-1 pt-2">
          <TextoCortado linhas={2} className="kicker">{rotulo}</TextoCortado>
        </h3>
        <Selo>{etiqueta}</Selo>
      </header>
      <div className="min-w-0 flex-1">
        {indisponivel ? (
          <div role="alert" className="mt-3">
            <p className="text-sm font-semibold text-perigo">{t('estados.indicadorErro')}</p>
            <p className="mt-1 text-xs break-words text-tinta-3">{t(i.falha === 'tempo' ? 'inicio.motivo.tempo' : 'inicio.motivo.erro')}</p>
            <Botao className="mt-2" tamanho="sm" onClick={aoTentar}>
              {t('estados.tentarDeNovo')}
            </Botao>
          </div>
        ) : (
          <Corpo i={i} moeda={moeda} rotuloPeriodo={rotuloPeriodo} />
        )}
      </div>
      {i.link && (
        <Link to={i.link} className="-mb-1 mt-2 flex items-center gap-1 self-end rounded-md px-1 py-1 text-xs font-medium text-acento-forte hover:underline">
          {t('inicio.verDetalhes')}
          <span className="sr-only">{`: ${rotulo}`}</span>
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      )}
    </article>
  );
}
